import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CART_STORAGE_KEY = 'posterly_cart_v1';
const REWARDS_STORAGE_KEY = 'posterly_rewards_v1';

// Flat delivery charge, the same however many posters are ordered.
// Waived once the Free Shipping tier is unlocked.
export const DELIVERY_CHARGE = 60;

// Offer tiers — thresholds in ₹. Order MUST be ascending.
export const OFFER_TIERS = [
  {
    id: 'free_shipping',
    threshold: 699,
    label: 'Free Shipping',
    reward: 'Free Shipping',
    icon: '🚚',
    needsPicker: false,
  },
  {
    id: 'free_poster',
    threshold: 999,
    label: 'Free Poster',
    reward: 'Any A4/A3 Poster — on us',
    icon: '🎁',
    needsPicker: true,
    // Reward is a normal poster from any non-split category
    filter: (p) => p.category !== 'Split Posters',
  },
  {
    id: 'free_split_poster',
    threshold: 1999,
    label: 'Free Split Poster',
    reward: 'Any Split Poster — on us',
    icon: '🔥',
    needsPicker: true,
    filter: (p) => p.category === 'Split Posters',
  },
];

const CartContext = createContext(null);

const safeParse = (raw, fallback) => {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed;
  } catch {
    return fallback;
  }
};

const loadCart = () => {
  if (typeof window === 'undefined') return [];
  const parsed = safeParse(window.localStorage.getItem(CART_STORAGE_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
};

const loadRewards = () => {
  if (typeof window === 'undefined') return {};
  const parsed = safeParse(window.localStorage.getItem(REWARDS_STORAGE_KEY), {});
  return parsed && typeof parsed === 'object' ? parsed : {};
};

const persist = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota errors */
  }
};

const makeLineKey = (productId, sizeLabel) => `${productId}::${sizeLabel}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadCart());
  // freeRewards is a map: { [tierId]: { productId, name, image_url, sizeLabel, category } }
  const [freeRewards, setFreeRewards] = useState(() => loadRewards());
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pickerTier, setPickerTier] = useState(null);

  useEffect(() => {
    persist(CART_STORAGE_KEY, items);
  }, [items]);
  useEffect(() => {
    persist(REWARDS_STORAGE_KEY, freeRewards);
  }, [freeRewards]);

  const addItem = useCallback((product, size, quantity = 1) => {
    if (!product || !size) return;
    const key = makeLineKey(product.id, size.label);
    setItems((prev) => {
      const existingIdx = prev.findIndex((it) => it.key === key);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: Math.min(99, next[existingIdx].quantity + quantity),
        };
        return next;
      }
      return [
        ...prev,
        {
          key,
          productId: product.id,
          name: product.name,
          category: product.category,
          image_url: product.image_url,
          sizeLabel: size.label,
          unitPrice: size.price,
          quantity: Math.min(99, quantity),
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) => {
      if (quantity <= 0) {
        return prev.filter((it) => it.key !== key);
      }
      return prev.map((it) =>
        it.key === key ? { ...it, quantity: Math.min(99, quantity) } : it,
      );
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setFreeRewards({});
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const openPicker = useCallback((tierId) => setPickerTier(tierId), []);
  const closePicker = useCallback(() => setPickerTier(null), []);

  const setReward = useCallback((tierId, product) => {
    if (!product) return;
    // Free item always uses the cheapest size for that product
    const sizes = product.sizes || [];
    const cheapest = sizes.length > 0
      ? [...sizes].sort((a, b) => a.price - b.price)[0]
      : { label: 'Standard', price: product.price };
    setFreeRewards((prev) => ({
      ...prev,
      [tierId]: {
        productId: product.id,
        name: product.name,
        category: product.category,
        image_url: product.image_url,
        sizeLabel: cheapest.label,
        unitPrice: cheapest.price, // shown for reference only, charged as ₹0
      },
    }));
    setPickerTier(null);
  }, []);

  const removeReward = useCallback((tierId) => {
    setFreeRewards((prev) => {
      if (!(tierId in prev)) return prev;
      const next = { ...prev };
      delete next[tierId];
      return next;
    });
  }, []);

  const { totalItems, paidTotal } = useMemo(() => {
    let count = 0;
    let paid = 0;
    for (const it of items) {
      count += it.quantity;
      paid += it.unitPrice * it.quantity;
    }
    return { totalItems: count, paidTotal: Math.round(paid * 100) / 100 };
  }, [items]);

  // Which tiers are currently unlocked based on paid subtotal
  const unlockedTierIds = useMemo(() => {
    return OFFER_TIERS.filter((t) => paidTotal >= t.threshold).map((t) => t.id);
  }, [paidTotal]);

  // Auto-remove any picked reward that is no longer unlocked
  useEffect(() => {
    setFreeRewards((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const tierId of Object.keys(prev)) {
        if (!unlockedTierIds.includes(tierId)) {
          delete next[tierId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [unlockedTierIds]);

  const nextTier = useMemo(() => {
    return OFFER_TIERS.find((t) => paidTotal < t.threshold) || null;
  }, [paidTotal]);

  const amountToNextTier = nextTier ? Math.max(0, nextTier.threshold - paidTotal) : 0;

  const deliveryCharge =
    items.length === 0 || unlockedTierIds.includes('free_shipping') ? 0 : DELIVERY_CHARGE;
  const grandTotal = paidTotal + deliveryCharge;

  const value = useMemo(
    () => ({
      items,
      totalItems,
      paidTotal,
      totalAmount: paidTotal, // for backward-compat with old drawer usage
      deliveryCharge,
      grandTotal,
      isDrawerOpen,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      openDrawer,
      closeDrawer,
      // Rewards
      freeRewards,
      unlockedTierIds,
      nextTier,
      amountToNextTier,
      setReward,
      removeReward,
      pickerTier,
      openPicker,
      closePicker,
    }),
    [
      items, totalItems, paidTotal, deliveryCharge, grandTotal, isDrawerOpen,
      addItem, updateQuantity, removeItem, clearCart, openDrawer, closeDrawer,
      freeRewards, unlockedTierIds, nextTier, amountToNextTier,
      setReward, removeReward, pickerTier, openPicker, closePicker,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
};
